import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { header } from '../../components/header.encry';
import StoreService from '../../components/StoreService';
import { getAuthData } from '../../utils/authStorage';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

const normalizePrinterHost = (remote) => {
  if (Array.isArray(remote?.printers)) {
    return {
      ...remote,
      store_ids: remote.store_ids || remote.data?.store_ids || [],
    };
  }

  const data = remote?.data || {};
  return {
    ...remote,
    computer_name: remote.name || data.computer_name || data.uniqueId || '',
    machine_uid: data.uniqueId || remote.machine_uid || remote._id || '',
    is_online: Boolean(remote.is_online ?? remote.is_valid),
    last_seen_at: remote.last_seen_at || remote.last_ping,
    store_ids: data.store_ids || remote.store_ids || [],
    printers: (data.printers || []).map((printer) => ({
      ...printer,
      printer_uid: printer.printer_uid || printer.id || printer.name,
      is_default: Boolean(printer.is_default),
      is_active: printer.is_active !== false,
      paper_sizes: printer.paper_sizes || [],
    })),
  };
};

const normalizeId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return String(value._id || value.id || value.$oid || value.value || '').trim();
  }
  return String(value).trim();
};

const getRemoteStoreIds = (remote) => (
  Array.isArray(remote?.store_ids)
    ? remote.store_ids
    : Array.isArray(remote?.data?.store_ids)
      ? remote.data.store_ids
      : []
).map(normalizeId).filter(Boolean);

const getAllowedStoreIds = () => {
  const authData = getAuthData() || {};
  const sessionStores = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('affected_store_ids') || '[]');
    } catch (error) {
      return [];
    }
  })();
  return [
    authData.default_store_id,
    sessionStorage.getItem('default_store_id'),
    ...(Array.isArray(authData.affected_store_ids) ? authData.affected_store_ids : []),
    ...(Array.isArray(sessionStores) ? sessionStores : []),
  ].map(normalizeId).filter(Boolean);
};

const filterPrintersForCurrentUser = (items, isAdminOrManager) => {
  if (isAdminOrManager) return items;
  const allowedStoreIds = new Set(getAllowedStoreIds());
  if (!allowedStoreIds.size) return [];

  return items.filter((remote) => {
    const remoteStoreIds = getRemoteStoreIds(remote);
    return remoteStoreIds.some((storeId) => allowedStoreIds.has(storeId));
  });
};

const buildTestPdfBase64 = () => {
  const now = new Date().toLocaleString();
  const pageWidth = 227; // 80 mm
  const pageHeight = 595; // 210 mm
  const escapePdfText = (value) => String(value).replace(/[\\()]/g, '\\$&');
  const lines = [
    { size: 13, x: 14, y: 560, text: 'Pegasus Remote Printing' },
    { size: 13, x: 14, y: 540, text: 'Test 80 mm' },
    { size: 9, x: 14, y: 512, text: now },
    { size: 9, x: 14, y: 486, text: 'Printer queue is working.' },
    { size: 9, x: 14, y: 468, text: 'Paper: 80 x 210 mm' },
    { size: 9, x: 14, y: 438, text: '--------------------------------' },
    { size: 10, x: 14, y: 412, text: 'If you can read this ticket,' },
    { size: 10, x: 14, y: 394, text: 'remote printing is OK.' },
    { size: 9, x: 14, y: 350, text: 'Total test: 1 page' }
  ];
  const stream = [
    '0.2 w',
    '14 528 m 213 528 l S',
    '14 372 m 213 372 l S',
    ...lines.map((line) => `BT /F1 ${line.size} Tf ${line.x} ${line.y} Td (${escapePdfText(line.text)}) Tj ET`)
  ].join('\n');
  const objects = [
    '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n',
    '2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n',
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`,
    '4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n',
    `5 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj\n`
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += object;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return btoa(pdf);
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.split(',')[1] : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

function RemotePrintersView() {
  const [printers, setPrinters] = useState([]);
  const [stores, setStores] = useState([]);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [generatedKey, setGeneratedKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [testPrinterUid, setTestPrinterUid] = useState('');
  const [testPaperName, setTestPaperName] = useState('');
  const [testQuantity, setTestQuantity] = useState(1);
  const [testPdfBase64, setTestPdfBase64] = useState('');
  const [submittingTest, setSubmittingTest] = useState(false);
  const roles = (() => {
    const authData = getAuthData();
    if (Array.isArray(authData?.roles)) return authData.roles;
    try {
      return JSON.parse(sessionStorage.getItem('roles') || '[]');
    } catch (error) {
      return [];
    }
  })();
  const isAdminOrManager = roles.includes('admin') || roles.includes('manager');

  const storeNamesById = stores.reduce((mapping, store) => {
    const id = normalizeId(store._id || store.id);
    if (id) {
      mapping[id] = store.name || store.label || store.storeid || id;
    }
    return mapping;
  }, {});

  const printerOptions = printers.flatMap((remote) => (
    (remote.printers || []).map((printer) => ({
      ...printer,
      computer_name: remote.computer_name || remote.machine_uid,
      remote_printer_id: remote._id
    }))
  ));

  const selectedPrinter = printerOptions.find((printer) => printer.printer_uid === testPrinterUid);

  const fetchPrinters = async () => {
    const response = await axios.get(`${API_URL}/printing/admin/printers`, header());
    const normalized = (response.data?.printers || []).map(normalizePrinterHost);
    return filterPrintersForCurrentUser(normalized, isAdminOrManager);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [printersData, storesData] = await Promise.all([
        fetchPrinters(),
        StoreService.fetchStores().catch(() => []),
      ]);
      setPrinters(printersData);
      setStores(Array.isArray(storesData) ? storesData : []);

      if (isAdminOrManager) {
        const [queueRes, historyRes] = await Promise.all([
          axios.get(`${API_URL}/printing/admin/queue`, header()).catch(() => ({ data: { jobs: [] } })),
          axios.get(`${API_URL}/printing/admin/history`, header()).catch(() => ({ data: { history: [] } })),
        ]);
        setQueue(queueRes.data?.jobs || []);
        setHistory(historyRes.data?.history || []);
      } else {
        setQueue([]);
        setHistory([]);
      }

      const firstPrinter = printersData?.flatMap((remote) => remote.printers || [])?.[0];
      if (!testPrinterUid && firstPrinter?.printer_uid) {
        setTestPrinterUid(firstPrinter.printer_uid);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erreur lors du chargement des imprimantes.');
    } finally {
      setLoading(false);
    }
  };

  const generateKey = async () => {
    if (!isAdminOrManager) {
      toast.error('Action reservee aux administrateurs et managers.');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/printing/admin/keys`, {}, header());
      setGeneratedKey(response.data?.auth_key || '');
      toast.success('Cle imprimante generee.');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erreur lors de la generation de cle.');
    }
  };

  const updatePrinterStores = async (remote, selectedStoreIds) => {
    if (!isAdminOrManager) {
      return;
    }

    const printerId = remote?._id || remote?.id;
    if (!printerId) {
      toast.error('Identifiant imprimante introuvable.');
      return;
    }

    const storeIds = selectedStoreIds.map(normalizeId).filter(Boolean);
    const previousPrinters = printers;
    setPrinters((current) => current.map((item) => (
      normalizeId(item._id || item.id) === normalizeId(printerId)
        ? { ...item, store_ids: storeIds, data: { ...(item.data || {}), store_ids: storeIds } }
        : item
    )));

    try {
      await axios.put(`${API_URL}/printing/admin/printers/${printerId}`, {
        store_ids: storeIds,
      }, header());
      toast.success('Magasins lies a l imprimante.');
    } catch (error) {
      setPrinters(previousPrinters);
      toast.error(error?.response?.data?.error || 'Impossible de mettre a jour les magasins.');
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') {
      toast.error('Merci de choisir un fichier PDF.');
      return;
    }
    try {
      setTestPdfBase64(await fileToBase64(file));
      toast.success('PDF charge pour le test.');
    } catch (error) {
      toast.error('Impossible de lire le PDF.');
    }
  };

  const submitTestPrint = async (useGeneratedPdf = false) => {
    if (!testPrinterUid) {
      toast.error('Selectionnez une imprimante.');
      return;
    }

    const contentBase64 = useGeneratedPdf ? buildTestPdfBase64() : testPdfBase64;
    if (!contentBase64) {
      toast.error('Chargez un PDF ou utilisez le PDF de test.');
      return;
    }

    setSubmittingTest(true);
    try {
      await axios.post(`${API_URL}/printing/admin/queue`, {
        printer_uid: testPrinterUid,
        content_base64: contentBase64,
        quantity: Math.max(1, Number(testQuantity) || 1),
        paper_size: testPaperName ? { name: testPaperName } : undefined,
        metadata: {
          source: 'remote_printers_test',
          created_from_front: true
        }
      }, header());
      toast.success('Job de test ajoute a la printing queue.');
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erreur lors de la creation du job test.');
    } finally {
      setSubmittingTest(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Remote Printers</h2>
          <div className="text-muted">Detection des imprimantes, ping, queue et historique.</div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={fetchData} disabled={loading}>
            <i className="fa-solid fa-rotate me-2"></i>
            Actualiser
          </button>
          {isAdminOrManager && (
            <button className="btn btn-primary" onClick={generateKey}>
              <i className="fa-solid fa-key me-2"></i>
              Generer une cle
            </button>
          )}
        </div>
      </div>

      {generatedKey && (
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-bold">Cle a copier dans PrinterConfig.json</div>
            <code>{generatedKey}</code>
          </div>
          <button className="btn btn-sm btn-outline-primary" onClick={() => navigator.clipboard?.writeText(generatedKey)}>
            Copier
          </button>
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Postes connectes</div>
              <div className="display-6 fw-bold">{printers.length}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Jobs en queue</div>
              <div className="display-6 fw-bold">{queue.filter((job) => job.status === 'pending').length}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Impressions historisees</div>
              <div className="display-6 fw-bold">{history.length}</div>
            </div>
          </div>
        </div>
      </div>

      {isAdminOrManager && (
      <div className="card mb-4 border-primary-subtle">
        <div className="card-header fw-bold">
          <i className="fa-solid fa-vial me-2"></i>
          Test Printing Queue
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-lg-4">
              <label className="form-label">Imprimante cible</label>
              <select className="form-select" value={testPrinterUid} onChange={(event) => {
                setTestPrinterUid(event.target.value);
                setTestPaperName('');
              }}>
                <option value="">Selectionnez une imprimante</option>
                {printerOptions.map((printer) => (
                  <option key={printer.printer_uid} value={printer.printer_uid}>
                    {printer.computer_name} - {printer.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-3">
              <label className="form-label">Paper size</label>
              <select className="form-select" value={testPaperName} onChange={(event) => setTestPaperName(event.target.value)} disabled={!selectedPrinter}>
                <option value="">Par defaut</option>
                {(selectedPrinter?.paper_sizes || []).map((paper) => (
                  <option key={`${paper.name}-${paper.width}-${paper.height}`} value={paper.name}>
                    {paper.name} {paper.width && paper.height ? `(${paper.width}x${paper.height})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2">
              <label className="form-label">Quantite</label>
              <input className="form-control" type="number" min="1" value={testQuantity} onChange={(event) => setTestQuantity(event.target.value)} />
            </div>
            <div className="col-lg-3">
              <label className="form-label">PDF optionnel</label>
              <input className="form-control" type="file" accept="application/pdf,.pdf" onChange={handlePdfUpload} />
            </div>
            <div className="col-12 d-flex flex-wrap gap-2">
              <button className="btn btn-primary" onClick={() => submitTestPrint(true)} disabled={submittingTest || !testPrinterUid}>
                <i className="fa-solid fa-file-pdf me-2"></i>
                Lancer PDF de test
              </button>
              <button className="btn btn-outline-primary" onClick={() => submitTestPrint(false)} disabled={submittingTest || !testPrinterUid || !testPdfBase64}>
                <i className="fa-solid fa-upload me-2"></i>
                Lancer le PDF charge
              </button>
              <span className="text-muted align-self-center">
                Le PC ReceiptPrinter imprimera ce job au prochain polling si la cle est configuree.
              </span>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="card mb-4">
        <div className="card-header fw-bold">Postes et imprimantes detectees</div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Ordinateur</th>
                <th>Status</th>
                <th>Dernier ping</th>
                <th>Magasins lies</th>
                <th>Imprimantes</th>
              </tr>
            </thead>
            <tbody>
              {printers.map((remote) => (
                <tr key={remote._id}>
                  <td>
                    <div className="fw-bold">{remote.computer_name || remote.machine_uid}</div>
                    <div className="small text-muted">{remote.machine_uid}</div>
                  </td>
                  <td>
                    <span className={`badge ${remote.is_online ? 'bg-success' : 'bg-secondary'}`}>
                      {remote.is_online ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td>{remote.last_seen_at ? new Date(remote.last_seen_at).toLocaleString() : '-'}</td>
                  <td style={{ minWidth: 260 }}>
                    {isAdminOrManager ? (
                      <select
                        className="form-select"
                        multiple
                        value={getRemoteStoreIds(remote)}
                        onChange={(event) => updatePrinterStores(
                          remote,
                          Array.from(event.target.selectedOptions || []).map((option) => option.value)
                        )}
                        size={Math.min(6, Math.max(3, stores.length || 3))}
                      >
                        {stores.map((store) => {
                          const storeId = normalizeId(store._id || store.id);
                          return (
                            <option key={storeId} value={storeId}>
                              {store.name || store.label || store.storeid || storeId}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <div className="d-flex flex-wrap gap-1">
                        {getRemoteStoreIds(remote).map((storeId) => (
                          <span key={storeId} className="badge bg-light text-dark border">
                            {storeNamesById[storeId] || storeId}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {(remote.printers || []).map((printer) => (
                      <div key={printer.printer_uid} className="mb-2">
                        <span className="fw-semibold">{printer.name}</span>
                        {printer.is_default && <span className="badge bg-primary ms-2">Default</span>}
                        <div className="small text-muted">
                          {(printer.paper_sizes || []).slice(0, 6).map((paper) => paper.name).filter(Boolean).join(', ')}
                          {(printer.paper_sizes || []).length > 6 ? ' ...' : ''}
                        </div>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
              {!printers.length && (
                <tr><td colSpan="5" className="text-center text-muted py-4">Aucun poste detecte.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAdminOrManager && (
      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header fw-bold">Printing Queue</div>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead><tr><th>Printer</th><th>Status</th><th>Qty</th><th>Date</th></tr></thead>
                <tbody>
                  {queue.slice(0, 20).map((job) => (
                    <tr key={job._id}>
                      <td>{job.printer_name || job.printer_uid}</td>
                      <td>{job.status}</td>
                      <td>{job.quantity}</td>
                      <td>{job.createdAt ? new Date(job.createdAt).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                  {!queue.length && <tr><td colSpan="4" className="text-center text-muted py-4">Queue vide.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header fw-bold">Printing History</div>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead><tr><th>Printer</th><th>Status</th><th>Qty</th><th>Date</th></tr></thead>
                <tbody>
                  {history.slice(0, 20).map((item) => (
                    <tr key={item._id}>
                      <td>{item.printer_name || item.printer_uid}</td>
                      <td>{item.status}</td>
                      <td>{item.quantity}</td>
                      <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                  {!history.length && <tr><td colSpan="4" className="text-center text-muted py-4">Historique vide.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default RemotePrintersView;
